import { useConfigStore } from "../Stores/Config";

const RECAPTCHA_SITE_KEY = "6LeMpiwcAAAAAAuGag4PWJvwSSgH0mCVX7EDQIjT";
const RECAPTCHA_TIMEOUT_MS = 3000;

export const validateRecaptchaAction = (
  action: string,
): Promise<string | undefined> => {
  const config = useConfigStore.getState().config;

  if (!config || !config.spamProtection) {
    return Promise.resolve(undefined);
  }

  // reCAPTCHA must never be able to block a send. If the script is missing
  // (blocked by CSP/firewall/ad-blocker or not loaded yet), fails, or hangs,
  // we resolve without a token and let the request proceed — the server still
  // applies its own spam validation where it matters.
  return new Promise((resolve) => {
    const grecaptcha = (window as any).grecaptcha;

    if (!grecaptcha) {
      resolve(undefined);
      return;
    }

    let settled = false;
    const finish = (token: string | undefined) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(token);
    };

    const timeout = setTimeout(() => finish(undefined), RECAPTCHA_TIMEOUT_MS);

    try {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then((token: string) => {
            clearTimeout(timeout);
            finish(token);
          })
          .catch(() => {
            clearTimeout(timeout);
            finish(undefined);
          });
      });
    } catch (error) {
      clearTimeout(timeout);
      finish(undefined);
    }
  });
};

const RECAPTCHA_SCRIPT_ID = "recaptchaScript";

/** Inject the reCAPTCHA v3 script once (same script as the spam-protection path). */
export const ensureRecaptchaLoaded = () => {
  try {
    if ((window as any).grecaptcha || document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    document.head.appendChild(script);
  } catch (exp) {}
};

/**
 * reCAPTCHA token for `action`, independent of config.spamProtection. With
 * `required` the script is loaded on demand and waited for (up to ~5s);
 * otherwise a token is only produced when reCAPTCHA is already there. Never
 * rejects: resolves undefined when no token can be had.
 */
export const getRecaptchaToken = async (
  action: string,
  required: boolean,
): Promise<string | undefined> => {
  if (required) ensureRecaptchaLoaded();
  const deadline = Date.now() + (required ? 5000 : 0);
  while (!(window as any).grecaptcha && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  const grecaptcha = (window as any).grecaptcha;
  if (!grecaptcha) return undefined;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (token: string | undefined) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };
    const timeout = setTimeout(() => finish(undefined), RECAPTCHA_TIMEOUT_MS);
    try {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then((token: string) => {
            clearTimeout(timeout);
            finish(token);
          })
          .catch(() => {
            clearTimeout(timeout);
            finish(undefined);
          });
      });
    } catch (exp) {
      clearTimeout(timeout);
      finish(undefined);
    }
  });
};
